import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCountryCode } from '@/modules/common/store/selectors';
import { loadDriverDocDefinitions } from '@/modules/driver/actions/async/load-driver-doc-definitions';
import { prepareUploadMyDriverForm } from '@/modules/driver/actions/async/prepare-upload-driver-form';
import { IDriverBrief } from '@/modules/driver/model';
import { DriverDocType } from '@/modules/driver/model/driver-doc-type';
import { getMyDriver } from '@/modules/driver/store/selectors';
import { myDriverUploadSelectors } from '@/modules/driver/store/upload-slice';
import { submitHelloStep } from '@/modules/onboarding/actions/submitHelloStep';
import type { IDraft, TFsmEvent } from '@/modules/onboarding/fsm-types';
import { IHelloForm } from '@/modules/onboarding/model/helloStep';
import { failedStepResult, isStepAccepted } from '@/modules/onboarding/model/stepErrors';
import { fsmSelectors } from '@/modules/onboarding/store/fsm-slice';
import { IPartner } from '@/modules/partner/model';
import { getPartner } from '@/modules/partner/store/selectors';
import type { IFormProps } from '@/pages/Wizard/view-types';
import { AppDispatch } from '@/root-store';
import { eventTracking } from '@/services/event-tracking';
import { CountryCode, TCountryCodes } from '@/shared/models/country-code';
import { getAvailableGenders } from '@/shared/models/gender';
import { LicensingAuthorityTypes } from '@/shared/models/licensingAuthority';
import { formatAddress } from '@/shared/utils/formatAddress';
import { useForm } from '@/shared/view/Form';
import { formMemoryStorage } from '@/shared/view/Form/formMemoryStorage';
import { scrollToFirstError } from '@/shared/view/scroll';
import { useCollapsedDocsList } from '@/shared/view/Upload/SupplementaryDocs/useCollapsedDocsList';
import { HandleDocListItemClick } from '@src-common/view/components/DocsList';

import { DriverForm, IDriverViewProps } from './Form';
import { useAcceptDvlaDeclaration } from './hooks/useAcceptDvlaDeclaration';

type AddressDraft = Pick<
  IHelloForm,
  | 'homeAddressLine1'
  | 'homeAddressLine2'
  | 'homeAddressCity'
  | 'homeAddressState'
  | 'homeAddressPostcode'
>;

const addressDraftKey = (partnerId: string) => `addressDraft_${partnerId}`;

const saveAddressDraft = (partnerId: string, values: AddressDraft) => {
  formMemoryStorage.setItem(addressDraftKey(partnerId), values);
};

const restoreAddressDraft = (partnerId: string, homeAddress: string): AddressDraft | null => {
  const draft = formMemoryStorage.getItem(addressDraftKey(partnerId)) as AddressDraft | undefined;

  if (!draft) {
    return null;
  }

  return formatAddress(draft) === homeAddress ? draft : null;
};

interface Props extends IFormProps<TFsmEvent> {
  View?: React.FC<IDriverViewProps>;
}

const mapDriverToForm = (
  {
    licenseNumber,
    ukNationalInsurance,
    birthday,
    gender,
    licensingAuthority,
    homeAddress,
  }: IDriverBrief,
  partner: IPartner,
  draft: IDraft,
  countryCode?: TCountryCodes,
): IHelloForm => {
  const savedAddress = restoreAddressDraft(partner.id, homeAddress);

  return {
    birthday,
    // A value the country doesn't offer has to be chosen again
    gender: gender && getAvailableGenders(countryCode).includes(gender) ? gender : null,
    licenseNumber,
    ukNationalInsurance,
    homeAddressLine1: savedAddress?.homeAddressLine1 ?? '',
    homeAddressLine2: savedAddress?.homeAddressLine2 ?? '',
    homeAddressCity: savedAddress?.homeAddressCity ?? '',
    homeAddressState: savedAddress?.homeAddressState ?? '',
    homeAddressPostcode: savedAddress?.homeAddressPostcode ?? '',
    personalInn: partner.personalInn,
    licensingAuthority:
      countryCode === CountryCode.UnitedStates
        ? null
        : draft.licensingAuthority || licensingAuthority || LicensingAuthorityTypes.TFL,
  };
};

const Driver: React.FC<Props> = ({ onSwitchScreen, View = DriverForm }) => {
  const dispatch = useDispatch() as AppDispatch;

  const driver = useSelector(getMyDriver);
  const partner = useSelector(getPartner);
  const draft = useSelector(fsmSelectors.getDraft);
  const countryCode = useSelector(getCountryCode);

  // the tracking event counts every document, so only the list is collapsed
  const docs = useSelector(myDriverUploadSelectors.getDocsList);
  const listDocs = useCollapsedDocsList(docs, 'myDriver');

  useEffect(() => {
    dispatch(loadDriverDocDefinitions());
  }, [dispatch]);

  const submitAndProceed = useCallback(
    async (formValues: IHelloForm, onSuccess: () => void) => {
      const resultAction = await dispatch(submitHelloStep(formValues));

      if (!submitHelloStep.fulfilled.match(resultAction)) {
        return failedStepResult(resultAction.error);
      }

      const { isSucceed, result = {} } = resultAction.payload;

      if (isSucceed && isStepAccepted(result)) {
        saveAddressDraft(partner.id, {
          homeAddressLine1: formValues.homeAddressLine1,
          homeAddressLine2: formValues.homeAddressLine2,
          homeAddressCity: formValues.homeAddressCity,
          homeAddressState: formValues.homeAddressState,
          homeAddressPostcode: formValues.homeAddressPostcode,
        });

        onSuccess();
      }

      return result;
    },
    [dispatch, partner.id],
  );

  const postForm = useCallback(
    (formValues: IHelloForm) =>
      submitAndProceed(formValues, () => {
        eventTracking.trackSnowplow({
          event: 'Onboarding - Personal - Success',
          state: docs.every(doc => doc.status !== 'missing'),
        });

        onSwitchScreen('NEXT');
      }),
    [submitAndProceed, onSwitchScreen, docs],
  );

  const acceptDvlaDeclaration = useAcceptDvlaDeclaration();

  const presubmitForm = useCallback(
    async (formValues: IHelloForm) => {
      // Check DVLA declaration consent: true if already agreed,
      // otherwise show the modal and wait for the user's choice.
      const dvlaDeclarationAccepted = await acceptDvlaDeclaration();

      // Not accepted — skip submit without surfacing an error.
      if (!dvlaDeclarationAccepted) {
        return {};
      }

      return postForm(formValues);
    },
    [acceptDvlaDeclaration, postForm],
  );

  const { handleSubmit, form, showSubmitErrors } = useForm({
    preSaveStore: {
      storeId: `helloStepForm-${partner.id}`,
      type: 'memory',
    },
    onSubmit: presubmitForm,
    defaultValues: mapDriverToForm(driver, partner, draft, countryCode),
  });

  const {
    control,
    formState: { isSubmitting, isDirty },
  } = form;

  const handleClickDocListItem = useCallback<HandleDocListItemClick>(
    (event, doc) =>
      void form.handleSubmit(async formValues => {
        const openUpload = () =>
          dispatch(
            prepareUploadMyDriverForm({
              docType: doc.docType as DriverDocType,
              onSuccess: () => onSwitchScreen('UPLOAD'),
            }),
          );

        if (!isDirty) {
          openUpload();

          return;
        }

        const result = await submitAndProceed(formValues, () => {
          form.reset(formValues);
          openUpload();
        });

        showSubmitErrors(result);
      }, scrollToFirstError)(),
    [submitAndProceed, dispatch, form, onSwitchScreen, isDirty, showSubmitErrors],
  );

  return (
    <form onSubmit={handleSubmit}>
      <View
        docs={listDocs}
        onDocListItemClick={handleClickDocListItem}
        control={control}
        isSubmitting={isSubmitting}
      />
    </form>
  );
};

export { Driver };
